import { QUANT_QUALITY_PENALTY, findModel } from './catalog.js';
import { CLOUD_REFERENCES, type CloudReference, cloudQualityForTask } from './cloud.js';
import { estimateFit, recommendQuantForMachine, recommendRuntimeForMachine } from './fit.js';
import { recommendForMachine } from './recommend.js';
import type { MachineProfile, ModelProfile, TaskCategory } from './types.js';

export type RouteDecision = 'local' | 'cloud' | 'hybrid';

export interface TaskRoute {
  task: TaskCategory;
  decision: RouteDecision;
  localModel?: ModelProfile;
  cloudModel?: CloudReference;
  rationale: string;
  localQuality: number;
  cloudQuality: number;
  localTps: number;
  cloudTps: number;
  monthlyCloudCost: number;
}

export interface RoutingPlan {
  machineId: string;
  taskRoutes: TaskRoute[];
  summary: string;
  overallDecision: RouteDecision;
}

const DAILY_TASKS_BY_CATEGORY: Record<TaskCategory, number> = {
  coding: 120,
  review: 40,
  debugging: 50,
  reasoning: 25,
  docs: 20,
  'tool-use': 200,
};

const AVG_IN_TOK = 1500;
const AVG_OUT_TOK = 500;

function monthlyCost(ref: CloudReference, task: TaskCategory): number {
  const tasksPerMonth = (DAILY_TASKS_BY_CATEGORY[task] ?? 50) * 30;
  return (
    (ref.costPerMTokIn * AVG_IN_TOK * tasksPerMonth) / 1_000_000 +
    (ref.costPerMTokOut * AVG_OUT_TOK * tasksPerMonth) / 1_000_000
  );
}

function cloudPickFor(task: TaskCategory, budget: 'free' | 'low' | 'standard' | 'premium'): CloudReference {
  if (budget === 'free' || budget === 'low') {
    return CLOUD_REFERENCES.find((c) => c.id === 'gpt-4o-mini') ?? CLOUD_REFERENCES[0];
  }
  if (budget === 'premium' && (task === 'reasoning' || task === 'debugging')) {
    return CLOUD_REFERENCES.find((c) => c.id === 'claude-opus-4-7') ?? CLOUD_REFERENCES[0];
  }
  return CLOUD_REFERENCES.find((c) => c.id === 'claude-sonnet-4-6') ?? CLOUD_REFERENCES[0];
}

export interface PlanOpts {
  budget?: 'free' | 'low' | 'standard' | 'premium';
  preferLocal?: boolean;
}

export function planRouting(machine: MachineProfile, opts: PlanOpts = {}): RoutingPlan {
  const budget = opts.budget ?? 'standard';
  const tasks: TaskCategory[] = ['coding', 'review', 'debugging', 'reasoning', 'docs', 'tool-use'];
  const best = recommendForMachine(machine, 'best-coding', 1)[0];

  const taskRoutes: TaskRoute[] = tasks.map((task) => {
    const cloudModel = cloudPickFor(task, budget);
    const cloudQuality = cloudQualityForTask(cloudModel, task);
    const cloudCost = monthlyCost(cloudModel, task);

    if (!best) {
      return {
        task,
        decision: 'cloud',
        cloudModel,
        rationale: 'No local model fits this machine — route everything to cloud.',
        localQuality: 0,
        cloudQuality,
        localTps: 0,
        cloudTps: cloudModel.approxTps,
        monthlyCloudCost: cloudCost,
      };
    }

    const localModel = best.model;
    const localQuality = Math.max(
      0,
      localModel.baseQualityScore - QUANT_QUALITY_PENALTY[best.fit.quantization],
    );
    const localTps = best.fit.expectedTps;

    let decision: RouteDecision;
    let rationale: string;

    if (task === 'tool-use' && localTps < 15) {
      decision = 'cloud';
      rationale = `Tool-use chains many calls — ${localTps} tok/s would bottleneck. Route to ${cloudModel.displayName}.`;
    } else if (task === 'reasoning' && cloudQuality - localQuality > 8) {
      decision = 'cloud';
      rationale = `Reasoning quality gap is large (+${Math.round(cloudQuality - localQuality)}). Route to ${cloudModel.displayName}.`;
    } else if (opts.preferLocal || cloudQuality - localQuality <= 4) {
      decision = 'local';
      rationale = `Local model ${localModel.displayName} is within ${Math.round(cloudQuality - localQuality)} quality points — keep it local.`;
    } else if (cloudCost < 5) {
      decision = 'cloud';
      rationale = `Cloud cost for this task is tiny (~$${cloudCost.toFixed(2)}/mo) and quality is higher.`;
    } else {
      decision = 'hybrid';
      rationale = `Start local (${localModel.displayName}), escalate to ${cloudModel.displayName} when quality matters. Saves ~$${cloudCost.toFixed(0)}/mo.`;
    }

    return {
      task,
      decision,
      localModel,
      cloudModel,
      rationale,
      localQuality,
      cloudQuality,
      localTps,
      cloudTps: cloudModel.approxTps,
      monthlyCloudCost: cloudCost,
    };
  });

  const localCount = taskRoutes.filter((r) => r.decision === 'local').length;
  const cloudCount = taskRoutes.filter((r) => r.decision === 'cloud').length;
  const overallDecision: RouteDecision =
    localCount > cloudCount ? 'local' : cloudCount > localCount ? 'cloud' : 'hybrid';
  const summary =
    overallDecision === 'local'
      ? 'Your machine can handle most coding workloads locally — cloud as escalation path.'
      : overallDecision === 'cloud'
        ? 'Cloud is the right primary path on this hardware — use local for privacy-sensitive tasks only.'
        : 'Hybrid: local for hot loops and cheap tasks, cloud for high-quality review and reasoning.';

  return { machineId: machine.id, taskRoutes, summary, overallDecision };
}

export function pickLocalModelFor(
  machine: MachineProfile,
  task: TaskCategory,
): { model: ModelProfile; fit: ReturnType<typeof estimateFit> } | null {
  const runtime = recommendRuntimeForMachine(machine);
  const picks = recommendForMachine(
    machine,
    task === 'coding' || task === 'debugging' || task === 'review' ? 'best-coding' : 'best-on-machine',
    3,
  );
  for (const pick of picks) {
    if (task === 'tool-use' && pick.fit.expectedTps < 12) continue;
    if (pick.fit.verdict === 'no' || pick.fit.verdict === 'technical') continue;
    return { model: pick.model, fit: pick.fit };
  }
  const fallback = picks[0];
  if (!fallback) return null;
  const quant = recommendQuantForMachine(machine, fallback.model);
  return { model: fallback.model, fit: estimateFit(machine, fallback.model, quant, runtime) };
}

export function modelById(id: string): ModelProfile | undefined {
  return findModel(id);
}
